import autobahn from "autobahn";

import { initTracking } from "@/analytics/init";
import { authAPIUrl, isCloud, wampUrl } from "@/environment";
import { initFeatureToggles } from "@/featureToggles";
import { CompanyHandler } from "@/handlers/companyHandler";
import { LicenseHandler } from "@/handlers/licenseHandler";
import { captureExceptionWithContext, resetUser, setUser } from "@/sentry";
import AuthService from "@/services/auth.service";
import { UserService } from "@/services/user.service";
import { logoutAndRedirect } from "@/utils";
import { setupInactivityLogout } from "@/utils/inactivityLogout";
import { setLastCompanyCookie } from "@/utils/lastCompanyCookie";

export default function wsconnect({ store } = {}) {
  return new Promise((resolve, reject) => {
    const userPromise = new UserService(authAPIUrl)
      .getCurrentUser()
      .then((user) => initFeatureToggles(user.company).then(() => user));
    const licensePromise = new LicenseHandler().getCompanyLicense();

    const connection = new autobahn.Connection({
      url: wampUrl,
      realm: "realm1",
      authmethods: ["anonymous"],
      use_es6_promises: true,
    });

    connection.onopen = async (_session, details) => {
      try {
        store.setUserInfo(details);
        store.setConnection(connection);
        store.available();

        const user = await userPromise;
        store.setUser(user);
        setUser(user);
        if (isCloud && user.company) setLastCompanyCookie(user.company);

        const company = await new CompanyHandler()
          .getCompany()
          .catch(() => ({ shouldOnboard: false }));
        store.setShouldOnboard(Boolean(company?.shouldOnboard));

        const license = (await licensePromise)?.data;
        if (license?.expiryDate) {
          license.sso ? store.enableSSO() : store.disableSSO();
          store.setOnPrem(license.onprem);
          store.setLicense(license);
          store.fetchLicenseUsage();

          initTracking(user, license);
        }
        void initInactivityTimeout();
        resolve(connection);
      } catch (err) {
        reject(err);
      }
    };

    // This callback function cannot be async because autobahnjs doesn't support Promises as return value
    connection.onclose = (reason, details) => {
      resetUser();

      switch (reason) {
        case "unreachable":
          store.notAvailable();
          return resolve();
        case "lost":
          // Try to reconnect by returning falsy value
          // https://github.com/crossbario/autobahn-js/blob/master/doc/reference.md#connection-callbacks
          store.notAvailable();
          return false;
        case "closed":
          if (!details.reason) {
            return true;
          }
          if (details.reason === "ch.rentouch.auth.expired_access_token") {
            return (async function () {
              try {
                await AuthService.refreshToken();
              } catch (err) {
                return captureErrorAndReject("Failed to refresh token", err);
              }
              return resolve(wsconnect({ store }));
            })();
          }
          if (
            details.reason === "ch.rentouch.auth.cookies_not_found" ||
            details.reason === "ch.rentouch.auth.denied" ||
            details.reason === "jwtRevoked"
          ) {
            return reject(new Error(`Connection closed, reason: ${details.reason}`));
          }
          return resolve();
      }

      // For all other reasons capture error and reject
      return captureErrorAndReject(reason, details);

      function captureErrorAndReject(reason, details) {
        const error = new Error(`Connection closed, reason: ${reason}`);

        if (details?.response?.status !== 401) {
          captureExceptionWithContext(error, {
            error: { ...details?.data },
            details,
          });
        }

        reject(error);
        return false;
      }
    };

    connection.open();
  });
}

/**
 * Fetch the company-wide auto logout setting and init a timeout that
 * will logout the user after the specified time (if necessary)
 */
export async function initInactivityTimeout() {
  const companyHandler = new CompanyHandler();
  const res = await companyHandler.getCompanySettings();
  if (!res.success) return;

  setupInactivityLogout({
    timeoutMinutes: res.data.autoLogoutAfter,
    onTimeout: () => logoutAndRedirect(),
  });
}
